import React from "react";
import { Box, Card, CardContent, Typography, Alert, Grid } from "@mui/material";
import { ProfitabilityMetrics } from "../types";

interface ProfitabilityCardProps {
	profitability: ProfitabilityMetrics | null;
	hasSellPrice: boolean;
}

const ProfitabilityCard: React.FC<ProfitabilityCardProps> = ({ profitability, hasSellPrice }) => {
	// If meal doesn't have a sell price, don't show profitability section
	if (!hasSellPrice) {
		return null;
	}

	// If there are missing cost data, show error message
	if (profitability && profitability.missingCostData.length > 0) {
		return (
			<Card sx={{ mt: 3 }}>
				<CardContent>
					<Typography variant="h6" gutterBottom>
						Profitability
					</Typography>
					<Alert severity="error" sx={{ mt: 2 }}>
						<Typography variant="body2" fontWeight="bold" gutterBottom>
							Cannot calculate profitability: Missing cost data for the following ingredients:
						</Typography>
						<Box component="ul" sx={{ mt: 1, mb: 0, pl: 2 }}>
							{profitability.missingCostData.map((ingredientName, index) => (
								<li key={index}>
									<Typography variant="body2">{ingredientName}</Typography>
								</li>
							))}
						</Box>
					</Alert>
				</CardContent>
			</Card>
		);
	}

	// If profitability data is available, display metrics
	if (!profitability) {
		return null;
	}

	// Format currency values
	const formatCurrency = (value: number) => {
		return `$${value.toFixed(2)}`;
	};

	// Format percentage
	const formatPercentage = (value: number) => {
		return `${value.toFixed(2)}%`;
	};

	// Determine profit color
	const getProfitColor = (percent: number) => {
		if (percent >= 30) return "success.main";
		if (percent >= 15) return "warning.main";
		return "error.main";
	};

	const hasWholesalePrice = profitability.wholesalePrice !== undefined && profitability.wholesalePrice !== null && profitability.wholesalePrice > 0;

	return (
		<Card sx={{ mt: 3 }}>
			<CardContent>
				<Typography variant="h6" gutterBottom>
					Profitability
				</Typography>

				{/* Cost Per Serve - Shown once at the top */}
				<Box sx={{ mt: 2, mb: 3 }}>
					<Typography variant="body2" color="text.secondary">
						Cost Per Serve
					</Typography>
					<Typography variant="h6">{formatCurrency(profitability.costPerServe)}</Typography>
				</Box>

				{/* Retail Profitability Section */}
				<Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
					Retail (RRP)
				</Typography>
				<Grid container spacing={2}>
					<Grid item xs={12} sm={4}>
						<Box>
							<Typography variant="body2" color="text.secondary">
								Sell Price (RRP)
							</Typography>
							<Typography variant="h6">{formatCurrency(profitability.sellPrice)}</Typography>
						</Box>
					</Grid>
					<Grid item xs={12} sm={4}>
						<Box>
							<Typography variant="body2" color="text.secondary">
								Sell Price (ex GST)
							</Typography>
							<Typography variant="h6">{formatCurrency(profitability.sellPriceExGST)}</Typography>
						</Box>
					</Grid>
					<Grid item xs={12} sm={4}>
						<Box>
							<Typography variant="body2" color="text.secondary">
								Gross Profit
							</Typography>
							<Typography variant="h6" sx={{ color: getProfitColor(profitability.grossProfitPercent) }}>
								{formatPercentage(profitability.grossProfitPercent)}
							</Typography>
						</Box>
					</Grid>
				</Grid>

				{/* Wholesale Profitability Section - Only show if wholesale price exists */}
				{hasWholesalePrice && profitability.wholesalePriceExGST && profitability.wholesaleGrossProfitPercent !== undefined && (
					<>
						<Typography variant="subtitle1" fontWeight="bold" sx={{ mt: 3, mb: 1 }}>
							Wholesale
						</Typography>
						<Grid container spacing={2}>
							<Grid item xs={12} sm={4}>
								<Box>
									<Typography variant="body2" color="text.secondary">
										Wholesale Price
									</Typography>
									<Typography variant="h6">{formatCurrency(profitability.wholesalePrice!)}</Typography>
								</Box>
							</Grid>
							<Grid item xs={12} sm={4}>
								<Box>
									<Typography variant="body2" color="text.secondary">
										Wholesale Price (ex GST)
									</Typography>
									<Typography variant="h6">{formatCurrency(profitability.wholesalePriceExGST)}</Typography>
								</Box>
							</Grid>
							<Grid item xs={12} sm={4}>
								<Box>
									<Typography variant="body2" color="text.secondary">
										Gross Profit
									</Typography>
									<Typography variant="h6" sx={{ color: getProfitColor(profitability.wholesaleGrossProfitPercent) }}>
										{formatPercentage(profitability.wholesaleGrossProfitPercent)}
									</Typography>
								</Box>
							</Grid>
						</Grid>
					</>
				)}
			</CardContent>
		</Card>
	);
};

export default ProfitabilityCard;
